import React, { useEffect, useState, useCallback } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
} from "@mui/material";
import {
  createItem,
  getCategories,
  updateItem,
  getUnits,
  getSizes,
} from "../../../Services/Services";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { debounce } from "lodash";

const AddItemForm = ({
  setItems,
  setOpenAddModal,
  onSuccess,
  mode = "page",
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const isEditMode = location.state?.mode === "edit";
  const editData = location.state?.data;

  const [formData, setFormData] = useState(
    editData
      ? [
          {
            name: editData.name,
            item_description: editData.item_description,
            category: editData.category
              ? { id: editData.category, name: editData.category_name }
              : null,
            size: editData.size
              ? { id: editData.size, size: editData.size_name }
              : null,
            item_code: editData.item_code,
            unit: editData.unit
              ? { id: editData.unit, name: editData.unit_name }
              : null,
            min_quantity: editData.min_quantity,
            current_stock: editData.current_stock || "",
            max_quantity: editData.max_quantity || "",
            status: editData.status || "active",
            price: editData.price || "",
            total_value: editData.total_value || "",
            created_by: editData.created_by,
            id: editData.id,
          },
        ]
      : [
          {
            unit: null,
            item_code: "",
            size: null,
            name: "",
            category: null,
            min_quantity: "",
            current_stock: "",
            max_quantity: "",
            status: "active",
            price: "",
            total_value: "",
            created_by: localStorage.getItem("user_id"),
          },
        ],
  );

  const [units, setUnits] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState({
    type: "",
    text: "",
  });

  // ── Fetch on mount ──
  useEffect(() => {
    fetchUnits();
    fetchAllCategories();
  }, []);

  // ── Edit mode me categories load ──
  useEffect(() => {
    if (isEditMode && editData?.size) {
      const sizeId =
        typeof editData.size === "object" ? editData.size.id : editData.size;
      fetchCategoriesByProductType(sizeId);
    }
  }, [isEditMode]);

  const fetchUnits = async () => {
    const data = await getUnits();
    setUnits(Array.isArray(data) ? data : data?.results || []);
  };

  const fetchSizes = async (search = "") => {
    try {
      const data = await getSizes(search);
      setSizes(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      console.error("Error fetching sizes:", error);
    }
  };

    const [allCategories, setAllCategories] = useState([]);
  const [filteredSizes, setFilteredSizes] = useState([]);

  const fetchAllCategories = async () => {
    try {
      const data = await getCategories(1, "");
      const cats = Array.isArray(data) ? data : data?.results || [];
      setAllCategories(cats);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchSizesByCategory = async (categoryId, search = "") => {
    if (!categoryId) {
      setFilteredSizes([]);
      return;
    }
    try {
      const data = await getSizes(search, categoryId);
      const sz = Array.isArray(data) ? data : data?.results || [];
      setFilteredSizes(sz);
    } catch (err) {
      setFilteredSizes([]);
    }
  };

  const fetchCategoriesByProductType = async (productTypeId, search = "") => {
    if (!productTypeId) {
      setCategories([]);
      return;
    }
    try {
      const data = await getCategories(1, search, productTypeId);
      setCategories(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      setCategories([]);
    }
  };

  const calculateTotalValue = (price, stockQty) => {
    const total = (parseFloat(price) || 0) * (parseFloat(stockQty) || 0);
    return total.toFixed(2);
  };

  const handleInputChange = (index, key, value) => {
    const updatedRows = [...formData];
    let row = { ...updatedRows[index] };

    switch (key) {
      case "category":
        row = { ...row, category: value, size: null };
        if (value?.id) {
          fetchSizesByCategory(value.id);
        } else {
          setFilteredSizes([]);
        }
        break;

      case "size":
        row = { ...row, size: value };
        break;

      case "unit":
        row.unit = value;
        break;

      case "price":
        row.price = value;
        row.total_value = calculateTotalValue(value, row.current_stock);
        break;

      case "current_stock":
        row.current_stock = value;
        row.total_value = calculateTotalValue(row.price, value);
        break;

      default:
        row[key] = value;
    }

    updatedRows[index] = row;
    setFormData(updatedRows);
  };

  // ── Debounced search ──
  const handleItemInputChange = useCallback(
    debounce(async (index, row, newValue, type) => {
      if (type === "category") {
        const productTypeId = row.size?.id;
        if (!productTypeId) {
          setCategories([]);
          return;
        }
        const catData = await getCategories(
          1,
          newValue?.trim() || "",
          productTypeId,
        );
        setCategories(
          Array.isArray(catData.results) ? catData.results : catData,
        );
      }

      if (type === "size") {
        await fetchSizes(newValue?.trim() || "");
      }
    }, 500),
    [],
  );

  const handleChange = (index, field, value) => {
    const updated = [...formData];
    updated[index][field] = value;
    setFormData(updated);
  };

  const addItemRow = () => {
    if (formData.length >= 5) {
      toast.error("⚠️ Maximum 5 items allowed!");
      return;
    }
    setFormData((prev) => [
      ...prev,
      {
        unit: null,
        item_code: "",
        size: null,
        name: "",
        item_description: "",
        category: null,
        min_quantity: "",
        current_stock: "",
        max_quantity: "",
        status: "active",
        price: "",
        total_value: "",
        created_by: localStorage.getItem("user_id"),
      },
    ]);
  };

  const removeItemRow = (index) => {
    const updated = [...formData];
    updated.splice(index, 1);
    setFormData(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponseMessage({ type: "", text: "" });

    try {
      const user_id = localStorage.getItem("user_id");
      const updatedFormData = formData.map((item) => ({
        ...item,
        category: item.category?.id,
        size: item.size?.id,
        unit: item.unit?.id,
        total_value: item.total_value,
        created_by: user_id,
      }));

      if (isEditMode) {
        await updateItem(editData.id, updatedFormData[0]);
        setResponseMessage({ type: "success", text: "Item updated!" });
      } else {
        await createItem(updatedFormData);
        setResponseMessage({ type: "success", text: "Items created!" });
      }

      if (onSuccess) await onSuccess();

      if (mode === "modal") {
        if (setOpenAddModal) setOpenAddModal(false);
      } else {
        // ✅ action ke basis pe navigate
        if (isEditMode) {
          navigate("/dashboard/item-list", { state: { action: "edited" } });
        } else {
          navigate("/dashboard/item-list", { state: { action: "created" } });
        }
      }
    } catch (error) {
      const errData = error.response?.data;
      if (errData) {
        Object.keys(errData).forEach((field) => {
          const messages = errData[field];
          if (Array.isArray(messages)) {
            messages.forEach((msg) => toast.error(msg));
          } else {
            toast.error(messages);
          }
        });
      } else {
        toast.error("Error occurred.");
      }
      setResponseMessage({
        type: "error",
        text: "Validation failed. Please check the form.",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderSizeAutocomplete = (item, index) => (
    <Autocomplete
      options={filteredSizes}
      getOptionLabel={(option) => option?.size || ""}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      value={item.size || null}
      disabled={!item.category}
      onChange={(e, val, reason) => {
        handleInputChange(index, "size", reason === "clear" ? null : val);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Product Type"
          required
          helperText={!item.category ? "Select Category first" : ""}
          InputProps={{ ...params.InputProps, sx: { height: 45 } }}
        />
      )}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 100px)",
        minHeight: 0,
      }}
    >
      {responseMessage.text && (
        <Alert severity={responseMessage.type} sx={{ mb: 2, flexShrink: 0 }}>
          {responseMessage.text}
        </Alert>
      )}

      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Buttons - fixed top, page/list scroll se independent */}
        <Box
          sx={{
            display: "flex",
            mt: { xs: 3, md: 0 },
            justifyContent: "flex-end",
            alignItems: isMobile ? "stretch" : "center",
            gap: 2,
            mb: 2,
            flexDirection: isMobile ? "column" : "row",
            flexShrink: 0,
            bgcolor: "background.paper",
            py: 1,
          }}
        >
          {!editData && (
            <Button
              variant="outlined"
              onClick={addItemRow}
              sx={{
                borderRadius: 0,
                height: 35,
                minWidth: 120,
                fontSize: "0.7rem",
                px: 2,
                textTransform: "none",
              }}
            >
              + Add More
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: 0,
              height: 35,
              minWidth: 120,
              fontSize: "0.7rem",
              px: 2,
              textTransform: "none",
            }}
          >
            ← Back
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{
              borderRadius: 0,
              height: 35,
              minWidth: 120,
              fontSize: "0.7rem",
              px: 2,
              textTransform: "none",
            }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Submit"
            )}
          </Button>
        </Box>

        {/* Form Fields - ye Box independently scroll karega */}
        <Box
          sx={{ overflowY: "auto", overflowX: "auto", flex: 1, minHeight: 0 }}
        >
          {formData.map((item, index) => (
            <Box
              key={index}
              sx={{
                mb: 3,
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                bgcolor: isDark
                  ? alpha(theme.palette.common.white, 0.04)
                  : "#fafafa",
              }}
            >
              <Grid container spacing={2}>
                {/* ✅ Category FIRST */}
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={allCategories}
                    getOptionLabel={(option) => option.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value?.id
                    }
                    value={item.category || null}
                    onChange={(e, val, reason) => {
                      handleInputChange(
                        index,
                        "category",
                        reason === "clear" ? null : val,
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category"
                        required
                        helperText={
                          !item.category ? "Select Category first" : ""
                        }
                        InputProps={{
                          ...params.InputProps,
                          sx: { height: 45 },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* ✅ Product Type SECOND (filtered by Category) */}
                <Grid item xs={12} sm={6} md={3}>
                  {renderSizeAutocomplete(item, index)}
                </Grid>

                {/* Unit */}
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={units}
                    getOptionLabel={(option) => option.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value?.id
                    }
                    value={item.unit || null}
                    onChange={(e, val) => handleInputChange(index, "unit", val)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="UOM"
                        required
                        InputProps={{
                          ...params.InputProps,
                          sx: { height: 45 },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Status */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={item.status}
                      label="Status"
                      onChange={(e) =>
                        handleChange(index, "status", e.target.value)
                      }
                      MenuProps={{
                        disableScrollLock: true,
                      }}
                      sx={{ height: 45 }}
                    >
                      <MenuItem value="Low">Low</MenuItem>
                      <MenuItem value="Normal">Normal</MenuItem>
                      <MenuItem value="High">High</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Item Name */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Item Name"
                    value={item.name}
                    onChange={(e) =>
                      handleChange(index, "name", e.target.value)
                    }
                    required
                    multiline
                    InputProps={{ sx: { minHeight: 45 } }}
                  />
                </Grid>

                {/* Stock Qty */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Stock Quantity"
                    type="number"
                    value={item.current_stock}
                    onChange={(e) =>
                      handleInputChange(index, "current_stock", e.target.value)
                    }
                    required
                    InputProps={{ sx: { height: 45 } }}
                  />
                </Grid>

                {/* Min Qty */}
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Min Quantity"
                    type="number"
                    value={item.min_quantity}
                    onChange={(e) =>
                      handleChange(index, "min_quantity", e.target.value)
                    }
                    required
                    InputProps={{ sx: { height: 45 } }}
                  />
                </Grid>

                {/* Max Qty */}
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Max Quantity"
                    type="number"
                    value={item.max_quantity}
                    onChange={(e) =>
                      handleChange(index, "max_quantity", e.target.value)
                    }
                    InputProps={{ sx: { height: 45 } }}
                  />
                </Grid>

                {/* Price */}
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Price"
                    type="number"
                    value={item.price}
                    onChange={(e) =>
                      handleInputChange(index, "price", e.target.value)
                    }
                    InputProps={{ sx: { height: 45 }, startAdornment: "₹" }}
                  />
                </Grid>

                {/* Total Value */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Total Value"
                    value={item.total_value}
                    disabled
                    InputProps={{
                      sx: {
                        height: 45,
                        bgcolor: isDark
                          ? alpha(theme.palette.action.disabledBackground, 0.5)
                          : "#f5f5f5",
                      },
                      startAdornment: "₹",
                    }}
                  />
                </Grid>

                {/* Remove Row */}
                {!editData && formData.length > 1 && (
                  <Grid item xs={12}>
                    <Button
                      color="error"
                      onClick={() => removeItemRow(index)}
                      variant="outlined"
                      fullWidth={isMobile}
                      sx={{ mt: 1 }}
                    >
                      Remove Item {index + 1}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Box>
          ))}
        </Box>
        <ToastContainer />
      </form>
    </Box>
  );
};

export default AddItemForm;
